import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { getBoardTypeConfig, getBoardRoutePath } from "@/lib/board-types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CopyPlus } from "lucide-react";

interface CopyToBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  sourceBoardId: string;
  boardType: string;
  entityLabel: string;
}

interface CopyableBoard {
  id: string;
  name: string;
  type: string;
}

export function CopyToBoardDialog({
  open,
  onOpenChange,
  taskId,
  sourceBoardId,
  boardType,
  entityLabel,
}: CopyToBoardDialogProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [targetBoardId, setTargetBoardId] = useState("");
  const [include, setInclude] = useState({
    description: true,
    checklists: true,
    animals: true,
    interactions: true,
    links: true,
    notes: true,
    attachments: true,
    dueDate: true,
    priority: true,
    labels: true,
  });

  const copyableUrl = `/api/task-boards/copyable?sourceType=${encodeURIComponent(boardType)}&excludeBoardId=${encodeURIComponent(sourceBoardId)}`;
  const { data: boardsData, isLoading: boardsLoading } = useQuery<{ boards: CopyableBoard[] }>({
    queryKey: [copyableUrl],
    enabled: open,
  });

  const copyMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/tasks/${taskId}/copy-to-board`, {
        targetBoardId,
        include,
      });
    },
    onSuccess: (data: { task: { id: string; type?: string } }) => {
      invalidateTaskQueries();
      const config = getBoardTypeConfig(data.task.type || boardType);
      const targetBoard = boardsData?.boards.find(b => b.id === targetBoardId);
      toast({
        title: `${entityLabel} copied`,
        description: (
          <span>
            Copied to {targetBoard?.name ?? "board"}.{" "}
            <a
              href={`${config.routePath}/${data.task.id}`}
              className="underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                setLocation(`${config.routePath}/${data.task.id}`);
              }}
              data-testid="link-copied-task"
            >
              View copy
            </a>
          </span>
        ),
      });
      onOpenChange(false);
      setTargetBoardId("");
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Copy failed",
        description: `Could not copy this ${entityLabel.toLowerCase()} to the selected board.`,
      });
    },
  });

  const boards = boardsData?.boards ?? [];

  const toggleInclude = (key: keyof typeof include) => {
    setInclude(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const checkboxOptions: { key: keyof typeof include; label: string }[] = [
    { key: "description", label: "Description" },
    { key: "checklists", label: "Checklists" },
    { key: "dueDate", label: boardType === "meeting" ? "Meeting Date" : "Due Date" },
    { key: "priority", label: "Priority" },
    { key: "labels", label: "Labels (matched by name)" },
    { key: "animals", label: "Linked Animals" },
    { key: "interactions", label: "Linked Interactions" },
    { key: "links", label: "Links" },
    { key: "notes", label: "Notes" },
    { key: "attachments", label: "Attachments" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-copy-to-board">
        <DialogHeader>
          <DialogTitle data-testid="text-copy-dialog-title">
            Copy {entityLabel} to Another Board
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="target-board" data-testid="label-target-board">Target Board</Label>
            {boardsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-loading-boards">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading boards...
              </div>
            ) : boards.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-no-boards">
                No other boards available. You need editor or admin access on another {boardType} board.
              </p>
            ) : (
              <Select value={targetBoardId} onValueChange={setTargetBoardId}>
                <SelectTrigger id="target-board" data-testid="select-target-board">
                  <SelectValue placeholder="Select a board..." />
                </SelectTrigger>
                <SelectContent>
                  {boards.map(board => (
                    <SelectItem key={board.id} value={board.id} data-testid={`select-item-board-${board.id}`}>
                      {board.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-2">
            <Label data-testid="label-components-to-copy">Components to Copy</Label>
            <div className="space-y-2">
              {checkboxOptions.map(opt => (
                <div key={opt.key} className="flex items-center gap-2">
                  <Checkbox
                    id={`include-${opt.key}`}
                    checked={include[opt.key]}
                    onCheckedChange={() => toggleInclude(opt.key)}
                    data-testid={`checkbox-include-${opt.key}`}
                  />
                  <Label
                    htmlFor={`include-${opt.key}`}
                    className="text-sm font-normal cursor-pointer"
                    data-testid={`label-include-${opt.key}`}
                  >
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="button-cancel-copy"
          >
            Cancel
          </Button>
          <Button
            onClick={() => copyMutation.mutate()}
            disabled={!targetBoardId || copyMutation.isPending}
            data-testid="button-confirm-copy"
          >
            {copyMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Copying...
              </>
            ) : (
              <>
                <CopyPlus className="w-4 h-4 mr-1" />
                Copy {entityLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
